import type { ProductTableHeaders } from '@hub/common/ProductTable/ProductTableHeader';
import { ProductTableSortingState } from '@hub/common/ProductTable/ProductTableHeader';
import type { TableData, TableRow } from '@hub/common/ProductTable/ProductTable';
import { ProductTable } from '@hub/common/ProductTable/ProductTable';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { UnitSystem } from '@igus/kopla-domain';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import type { FileMetadata, Product, StreamProduct } from '@igus/iglidur-free-form-hub-interfaces';
import { Procedures } from '@igus/iglidur-free-form-hub-interfaces';
import { MaterialInfo } from '../../common/material-info/MaterialInfo';
import { MaterialColorIcon } from '../ui/MaterialColorIcon';
import { DeliveryInfo } from '@hub/free-form-hub/free-form-hub-result/ui/DeliveryInfo';
import { PriceInfo } from '@hub/free-form-hub/free-form-hub-result/ui/PriceInfo';
import { MaterialTeaser } from '@hub/free-form-hub/free-form-hub-result/ui/MaterialTeaser';
import { Dialog } from '@hub/common/Dialog/Dialog';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { LifetimeBar } from '@hub/free-form-hub/free-form-hub-result/ui/LifetimeBar';
import type { ILifetimeSingleResult } from '@igus/tribology-domain';
import type { ILifetimeSingleResultSuccess } from '@igus/tribology-domain/src/lib/lifetime/lifetime-single-result';
import type { CurrentLifeTimeConfiguration } from '@hub/free-form-hub/common/lifetime/useLifetime';

type FreeFormHubTableProps = {
  productList: StreamProduct[];
  fileId: string;
  maxLifeTimeReference: number;
  materialsLifetime: { [key: string]: ILifetimeSingleResult };
  currentLifeTimeConfiguration: CurrentLifeTimeConfiguration;
  onNavigateToMaterialDetails: (row: TableRow) => void;
  metaData: FileMetadata;
  quantity: number;
  modelThumbnail?: string;
  showToast: (product: Product) => void;
  unitSystem: UnitSystem;
};

export const FreeFormHubTable = ({
  productList,
  fileId,
  maxLifeTimeReference,
  materialsLifetime,
  currentLifeTimeConfiguration,
  onNavigateToMaterialDetails,
  metaData,
  quantity,
  modelThumbnail,
  showToast,
  unitSystem,
}: FreeFormHubTableProps) => {
  const [selectedProduct, setSelectedProduct] = useState<StreamProduct>();
  const { CustomEvent } = usePiwikPro();
  const [tableData, setTableData] = useState<TableData>([]);
  const { t } = useTranslation(['free-form-hub/freeFormHub', 'common']);
  const headers: ProductTableHeaders[] = [
    {
      key: 'materialIcon',
      value: '',
      sortingState: ProductTableSortingState.NoSort,
      sortable: false,
      styles: 'max-w-[200px] min-w-[55px]',
    },
    {
      key: 'material',
      value: `${t(`HEADLINES.MATERIAL`, { unit: UnitSystem.metric, ns: 'free-form-hub/freeFormHub' })}`,
      sortingState: ProductTableSortingState.NoSort,
      sortable: true,
      styles: 'max-w-[200px] min-w-[55px]',
    },
    {
      key: 'infoModal',
      value: '',
      sortingState: ProductTableSortingState.NoSort,
      sortable: false,
      styles: 'max-w-[200px] min-w-[55px]',
    },
    {
      key: 'deliveryTime',
      value: `${t(`HEADLINES.DELIVERY_TIME`, { unit: UnitSystem.metric, ns: 'free-form-hub/freeFormHub' })}`,
      sortingState: ProductTableSortingState.NoSort,
      sortable: true,
      styles: 'max-w-[200px] min-w-[55px]',
    },
    {
      key: 'lifetime',
      value: `${t(`HEADLINES.LIFETIME`, { unit: UnitSystem.metric, ns: 'free-form-hub/freeFormHub' })}`,
      sortingState: ProductTableSortingState.NoSort,
      sortable: true,
      styles: 'max-w-[200px] min-w-[55px]',
      infoIcon: true,
    },
    {
      key: 'price',
      value: `${t(`HEADLINES.PRICE`, { ns: 'free-form-hub/freeFormHub' })} / ${t(`HEADLINES.PIECE`, {
        ns: 'free-form-hub/freeFormHub',
      })}`,
      sortingState: ProductTableSortingState.Ascending,
      sortable: true,
      styles: 'max-w-[200px] min-w-[55px] justify-self-center',
    },
  ];

  const materialInfoCell = (product?: StreamProduct) => (
    <button
      type="button"
      className="flex  text-gray-300 w-[20px] h-[20px] rounded-full"
      onClick={event => {
        event.stopPropagation();
        setSelectedProduct(product);
        CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.articleInfoOpen);
      }}
    >
      <Icon size={8} color={IconColor.GRAY} id={IconID.INFO_CIRCLE_MODERN} />
    </button>
  );

  const renderTable = (data: StreamProduct[]): TableData =>
    data.map(product => ({
      id: `${product.material.name}-${product.origin}-${product.material.procedure}-${fileId}`,
      cells: [
        {
          key: 'materialIcon',
          value: '',
          template: <MaterialColorIcon colorHex={product.material.colorHex} />,
        },
        {
          key: 'material',
          value: product.material.name,
          template: <MaterialTeaser material={product.material} origin={product.origin} />,
          styles: 'font-bold',
        },
        {
          key: 'infoModal',
          value: '',
          template: materialInfoCell(product),
        },
        {
          key: 'deliveryTime',
          value:
            product.delivery?.type === 'weeks' && typeof product.delivery.max === 'number'
              ? product.delivery.max * 7
              : (product.delivery?.max ?? -1),
          template: <DeliveryInfo delivery={product.delivery} isLoading={product.status === 'pending'} />,
        },
        {
          key: 'lifetime',
          value: (materialsLifetime?.[product.material.name] as ILifetimeSingleResultSuccess)?.lifetime ?? -1,
          template: (
            <LifetimeBar
              lifetime={(materialsLifetime?.[product.material.name] as ILifetimeSingleResultSuccess)?.lifetime || -1}
              maxLifeTimeReference={maxLifeTimeReference}
            />
          ),
        },
        {
          key: 'price',
          value: product.price?.unit ?? -1,
          styles: 'font-bold max-w-[150px] pr-4 pt-6 flex justify-end',
          template: (
            <PriceInfo
              uploadId={fileId}
              quantity={quantity}
              product={product}
              metaData={metaData}
              modelThumbnail={modelThumbnail}
              showToast={showToast}
            />
          ),
        },
      ],
    }));

  const showFootNote = () => {
    const hasToolCost = tableData && productList.find(product => product.price?.details?.toolCost > 0);
    const isCNCMaterial = productList.some(({ material }) => material.procedure === Procedures.CNC);

    return hasToolCost || isCNCMaterial;
  };

  useEffect(() => {
    window.document.body.classList[selectedProduct ? 'add' : 'remove']('overflow-hidden');
  }, [selectedProduct]);

  useEffect(() => {
    setTableData(renderTable(productList));
  }, [productList, materialsLifetime, maxLifeTimeReference]);

  return (
    <>
      <ProductTable
        headers={headers}
        tableData={tableData}
        currentLifeTimeConfiguration={currentLifeTimeConfiguration}
        defaultSorting={{ key: 'price', sortingState: ProductTableSortingState.Ascending }}
        onRowClick={row => onNavigateToMaterialDetails(row)}
        unitSystem={unitSystem}
      />
      {selectedProduct && (
        <Dialog visible={!!selectedProduct} fullScreen>
          <MaterialInfo
            material={selectedProduct.material}
            unitSystem={unitSystem}
            setShowMaterialInfo={() => setSelectedProduct(undefined)}
          />
        </Dialog>
      )}
      {showFootNote() && (
        <p data-testid="price-delivery-info" className="font-medium text-xs text-igus-primary my-6">
          * {t(`PRICE_AND_DELIVERY_TIMES_CHECK`, { ns: 'free-form-hub/freeFormHub' })}
        </p>
      )}
    </>
  );
};
